import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, inject} from '@angular/core';
import { AbstractStepDTO, ConditionIfStepDTO, ConfiguratorDataViewService, ConfiguratorLandingPageService, ConfiguratorTableService, DataViewDto, FieldTable, LandingPageEventStepDTO, LandingPageResult, Table } from '@tj/tj-dmp-lib-api';
import { EMPTY, Observable, of} from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { parseType } from '../../filter/filter.utils';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ENTRY_STEP_TOKEN, ON_CANCEL_TOKEN, ON_SAVE_TOKEN, PARTNER_TOKEN, READ_ONLY_MODE_TOKEN, STEP_TOKEN, TENANT_TOKEN } from '../injection-tokens';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import { Field, GroupField, SINGLE_SELECT_VALUE_TYPES } from '../../filter/filter.type';
import {resolveI18nLabel} from '../../../pipes/i18n-label.pipe';
import {LangService} from '../../../services/lang.service';
import { StepDescriptionService } from '../step-description.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'dmp-condition-step-config',
    templateUrl: './condition-step-config.component.html',
    styleUrls: ['./condition-step-config.component.scss'],
    standalone: false
})
export class ConditionStepConfigComponent extends AbstractStepConfigComponent implements OnInit, OnDestroy {
  private readonly stepDescriptionService = inject(StepDescriptionService);
  private readonly langService = inject(LangService);
  private readonly translateService = inject(TranslateService);
  private readonly landingPageService = inject(ConfiguratorLandingPageService);
  private readonly entryStep = inject(ENTRY_STEP_TOKEN, { optional: true });

  /** Champ du contexte où le script du formulaire écrit son résultat (événement « Formulaire soumis »). */
  static readonly FORM_RESULT_FIELD = 'landingpage.result';

  sourceOptions: { label: string; value: 'form' | 'data' }[] = [];
  formResults: { label: string; value: string }[] = [];

  /** Le scénario démarre par « Formulaire soumis » : la condition peut tester le résultat du formulaire. */
  get isFormEntry(): boolean {
    return this.entryStep?.type === AbstractStepDTO.TypeEnum.LANDINGPAGEEVENT
      && !!(this.entryStep as LandingPageEventStepDTO).idLandingPage;
  }

  get isFormSource(): boolean {
    return this.isFormEntry && this.conditionForm?.get('source')?.value === 'form';
  }

  conditionForm: FormGroup;

  availableViews: { label: string; value: string }[] = [];
  availableFields: GroupField[] = [];
  availableOperators: { label: string; value: string }[] = [];

  commandTable: string | null = null;

  // Types d'opérateurs disponibles
  private stringOperators: {value:string, label: string} [] = []; 
  private numberOperators: {value:string, label: string} [] = [];
  private dateOperators: {value:string, label: string} [] = []
  private booleanOperators: {value:string, label: string} [] = []

  get conditionStep(): ConditionIfStepDTO {
    return this.step as ConditionIfStepDTO;
  }

  get viewId() { return this.conditionForm.get('viewId'); }
  get field() { return this.conditionForm.get('field'); }
  get operator() { return this.conditionForm.get('operator'); }
  get value() { return this.conditionForm.get('value'); }

  get rangeArray(): FormArray {
    const valueControl = this.conditionForm.get('value');
    return valueControl instanceof FormArray ? valueControl : null;
  }

  get rangeMinControl(): FormControl {
    return this.rangeArray?.at(0) as FormControl || null;
  }

  get rangeMaxControl(): FormControl {
    return this.rangeArray?.at(1) as FormControl || null;
  }

  get tagSelectValue(): any {
    return this.conditionForm.get('value')?.value;
  }

  set tagSelectValue(newValue: any) {
    this.conditionForm.get('value')?.setValue(newValue);
    this.markAsChanged();
  }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private formBuilder: FormBuilder,
    private dataViewService: ConfiguratorDataViewService,
    private tableService: ConfiguratorTableService,
    private cdr: ChangeDetectorRef
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.setupFormSubscription();
    this.setupSourceSubscription();
    this.loadAvailableViews();
    this.initializeOperatorsByType();
    this.loadFormResults();
  }

  protected updateStepFromForm(): void {
    if (!this.conditionStep || !this.conditionForm.valid) return;

    if (this.isFormSource) {
      const code = this.conditionForm.get('formResult').value;
      this.conditionStep.idDataView = undefined;
      this.conditionStep.field = ConditionStepConfigComponent.FORM_RESULT_FIELD;
      this.conditionStep.operator = undefined;
      this.conditionStep.values = code ? [code] : [];
      this.conditionStep.excludeYear = undefined;
      this.conditionStep.description = this.stepDescriptionService.formResultCondition(
        this.formResults.find(result => result.value === code)?.label ?? code);
      return;
    }

    const { viewId, field, operator, value, excludeYear } = this.conditionForm.value;

    let description = this.generateReadableDescription(field, operator, value, excludeYear);
    this.conditionStep.description = description;

    this.conditionStep.idDataView = viewId;
    this.conditionStep.field = field.fullName;

    if (field.valueType === 'TAG' || ['list', 'boolean'].includes(field.variableType)) {
      this.conditionStep.operator = undefined;

      if (value) {
        if (Array.isArray(value)) {
          this.conditionStep.values = value
            .filter(v => v !== null && v !== undefined)
            .map(v => typeof v === 'object' && v.code ? v.code : String(v));
        } else if (typeof value === 'object' && value.code) {
          this.conditionStep.values = [value.code];
        } else {
          this.conditionStep.values = [String(value)];
        }
      } else {
        this.conditionStep.values = [];
      }
      return;
    }

    this.conditionStep.operator = operator;

    // Gestion des valeurs selon le type
    if (this.isEmptyOperator()) {
      this.conditionStep.values = [];
    } else if (Array.isArray(value)) {
      this.conditionStep.values = value
        .filter(v => v !== null && v !== undefined && v !== '')
        .map(v => String(v));
    } else if (value !== null && value !== undefined && value !== '') {
      this.conditionStep.values = [String(value)];
    } else {
      this.conditionStep.values = [];
    }

    this.conditionStep.excludeYear = this.isDateRangeOperator || this.isDateSlidingRange ? excludeYear : undefined
  }

  protected isConfigurationValid(): boolean {
    if (this.isFormSource) {
      return !!this.conditionForm.get('formResult').value;
    }
    if (!this.conditionForm.valid) return false;

    const { viewId, field, operator, value } = this.conditionForm.value;

    // Vérification de base
    if (!viewId || !field) return false;

    // Pour TAG/list/booléen : pas besoin d'opérateur
    if (field.valueType === 'TAG' || ['list', 'boolean'].includes(field.variableType)) {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    }

    // Pour les autres : vérifier l'opérateur
    if (!operator) return false;

    // Si l'opérateur est "vide", pas besoin de valeur
    if (this.isEmptyOperator()) {
      return true;
    }

    // Vérification selon le type de champ et d'opérateur
    if (this.isRangeOperator()) {
      return Array.isArray(value) && value.length === 2 &&
        value.every(v => v !== null && v !== undefined && v !== '');
    }

    if (this.isChipsOperator()) {
      return Array.isArray(value) && value.length > 0;
    }

    if (this.isDateExactMode()) {
      return Array.isArray(value) && value.length > 0;
    }

    // Pour les autres cas, une valeur simple est requise
    return value !== null && value !== undefined && value !== '';
  }


  private createForm(): void {
    this.conditionForm = this.formBuilder.group({
      viewId: this.createFormControl('', [Validators.required]),
      field: this.createFormControl('', [Validators.required]),
      operator: this.createFormControl(''),
      value: this.createFormControl(null),
      excludeYear: this.createFormControl(false),
      source: this.createFormControl(this.initialSource()),
      formResult: this.createFormControl(this.isFormResultStep() ? this.conditionStep.values?.[0] ?? null : null)
    });
    this.applySourceValidators(this.conditionForm.get('source').value);

    if (!this.readOnlyMode) {
      this.conditionForm.valueChanges.pipe(
        takeUntil(this.destroy$),
      ).subscribe(() => {
        if (!this.isInitializing) {
          this.markAsChanged();
        }
      });
    }    
  }

  private setupFormSubscription(): void {

    this.viewId?.valueChanges.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged(),
      filter(viewId => !!viewId),
      tap(() => this.commandTable = null),
      switchMap(viewId => this.loadFieldsForViewAsObservable(viewId))
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.conditionForm.patchValue({
          field: '',
          operator: '',
          value: '',
          excludeYear: false
        });
      } else {
        let foundField = null;
        for (const group of this.availableFields) {
          foundField = group.items.find(f => `${f.schema}.${f.name}` === this.conditionStep.field);
          if (foundField) break;
        }
        this.conditionForm.patchValue({ field: foundField })
      }
    });

    this.field?.valueChanges.pipe(
      takeUntil(this.destroy$),
      filter(field => !!field),
      distinctUntilChanged()
    ).subscribe(field => {
      if (field.valueType === 'TAG' || ['list', 'boolean'].includes(field.variableType)) {
        this.availableOperators = [];
        if (!this.isInitializing) {
          this.conditionForm.patchValue({ operator: '' });
          this.conditionForm.setControl('value', this.formBuilder.control(this.emptyValueForField(field)));
        } else {
          if (!this.conditionForm.get('value')) {
            this.conditionForm.setControl('value', this.formBuilder.control(null));
          }
          this.initializeFormValues();
        }
      } else {
        this.loadOperatorsForField(field);
        if (!this.isInitializing) {
          this.conditionForm.patchValue({ operator: '', value: '', excludeYear: false });
        } else {
          this.conditionForm.patchValue({ operator: this.conditionStep.operator });
        }
      }
    });

    this.operator?.valueChanges.pipe(
      takeUntil(this.destroy$),
      filter(operator => !!operator),
      distinctUntilChanged()
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.onOperatorChange();
      } else {
        this.initializeFormValues();
      }
    });
  }

  private isFormResultStep(): boolean {
    return this.conditionStep?.field === ConditionStepConfigComponent.FORM_RESULT_FIELD;
  }

  /** Condition déjà enregistrée : sa source ; nouvelle condition d'un scénario « Formulaire soumis » : le résultat du formulaire. */
  private initialSource(): 'form' | 'data' {
    if (!this.isFormEntry) return 'data';
    return this.isFormResultStep() || !this.conditionStep?.field ? 'form' : 'data';
  }

  private setupSourceSubscription(): void {
    this.sourceOptions = [
      { label: this.translateService.instant('components.workflowStepConfig.conditionStepConfig.fields.source.form'), value: 'form' },
      { label: this.translateService.instant('components.workflowStepConfig.conditionStepConfig.fields.source.data'), value: 'data' }
    ];
    this.conditionForm.get('source').valueChanges.pipe(
      takeUntil(this.destroy$),
      distinctUntilChanged()
    ).subscribe(source => this.applySourceValidators(source));
  }

  /** La vue et le champ ne sont requis que pour tester une donnée. */
  private applySourceValidators(source: 'form' | 'data'): void {
    const dataSource = !this.isFormEntry || source !== 'form';
    ['viewId', 'field'].forEach(name => {
      const control = this.conditionForm.get(name);
      control.setValidators(dataSource ? [Validators.required] : []);
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /** Résultats possibles du formulaire qui démarre le scénario, avec leur libellé dans la langue courante. */
  private loadFormResults(): void {
    if (!this.isFormEntry || !this.tenant) return;
    const idLandingPage = (this.entryStep as LandingPageEventStepDTO).idLandingPage;
    this.landingPageService.getLandingPageUsingGET(this.tenant, idLandingPage).pipe(
      takeUntil(this.destroy$),
      catchError(() => of(null))
    ).subscribe(landingPage => {
      const lang = (this.langService.currentLang || 'fr').toUpperCase();
      this.formResults = (landingPage?.results ?? []).map((result: LandingPageResult) => ({
        label: result.labels?.[lang] || result.labels?.['FR'] || result.code,
        value: result.code
      }));
    });
  }

  private loadAvailableViews(): void {
    if (!this.tenant) return;

    this.dataViewService.getAllDataViewsUsingGET(this.tenant)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Erreur lors du chargement des vues:', error);
          return EMPTY;
        })
      ).subscribe(views => {
        this.availableViews = views
          .sort((a, b) => a.code.localeCompare(b.code))
          .map(view => ({ label: resolveI18nLabel(view.labels, this.langService.currentLang, view.name), value: view.id }));
        this.initializeForm();
        this.preselectEntryView(views);
      })
  }

  /**
   * Nouvelle condition d'un scénario qui démarre par une recherche sauvegardée : la vue de cette recherche, la seule
   * dont les données sont disponibles dans le scénario.
   */
  private preselectEntryView(views: DataViewDto[]): void {
    if (this.conditionStep.idDataView || this.entryStep?.type !== AbstractStepDTO.TypeEnum.SAVEQUERY) return;
    const codeDataView = (this.entryStep as any).codeDataView;
    const view = views.find(v => v.code === codeDataView);
    if (view) {
      this.conditionForm.patchValue({ viewId: view.id });
    }
  }

  private loadOperatorsForField(field: Field): void {
    this.availableOperators = [];

    if (field) {
      // Définir les opérateurs disponibles selon le type de champ
      switch (field.variableType) {
        case 'string':
          this.availableOperators = this.stringOperators;
          break;
        case 'number':
          this.availableOperators = this.numberOperators;
          break;
        case 'date':
          this.availableOperators = this.dateOperators;
          break;
        case 'boolean':
          this.availableOperators = this.booleanOperators;
          break;
        default:
          this.availableOperators = [];
      }
    }
  }

  private initializeForm(): void {
    if (!this.conditionStep.idDataView) return;

    this.isInitializing = true;

    // Initialiser la vue
    this.conditionForm.patchValue({
      viewId: this.conditionStep.idDataView
    });
  }

  private loadFieldsForViewAsObservable(viewId: string): Observable<GroupField[]> {
    const view = this.availableViews.find(v => v.value === viewId);
    if (!view) return of([]);

    return this.dataViewService.getDataViewUsingGET(this.tenant, this.partner, viewId, false).pipe(
      switchMap(view => {
        if (!view?.tablesUsed?.length) return of([]);
        this.initCommandTable(view);
        return this.tableService.getTablesUsingGET(this.tenant, view.tablesUsed);
      }),
      tap(tables => {
        this.loadFieldsFromTables(tables);
      }),
      map(() => this.availableFields),
      catchError(error => {
        console.error('Erreur chargement champs:', error);
        return of([]);
      })
    );
  }

  private loadFieldsFromTables(tables: Table[]) {
    this.availableFields = [];

    this.availableFields = tables
      .filter(t => t.code !== 'personal_affi_auth')
      .map((table) => ({
        label: table && table.label && table.label.trim().length > 0 ? table.label : table.code,
        value: table.code,
        items: table.fields
          .filter(f => f.categoryField !== FieldTable.CategoryFieldEnum.TECHNICAL)
          .map((field) => ({
            name: field.code,
            label: field.label ?? field.code,
            variableType: parseType(field.categoryField, field.type),
            valueType: field.categoryField,
            schema: table.code,
            fullName: `${table.code}.${field.code}`
          } as Field & { fullName: string }))
      }));
  }

  private initializeOperatorsByType(): void {
    this.stringOperators = this.buildOperatorListForType('STRING_');
    this.numberOperators = this.buildOperatorListForType('NUMBER_');
    this.dateOperators = this.buildOperatorListForType('DATE_');
    this.booleanOperators = this.buildOperatorListForType('BOOLEAN_');
  }

  private buildOperatorListForType(type: string): { value: string, label: string }[] {
    return Object.values(ConditionIfStepDTO.OperatorEnum)
      .filter((value: string) => value.startsWith(type))
      .map((value: string) => ({
        value: value,
        label: this.getOperatorLabel(value) 
      }));
  }

  private initCommandTable(view: DataViewDto): void {
    if (view.table.tableName === 'command') {
      for (const table of view.table.join) {
        if (['flight', 'carrental', 'hotel', 'parking'].includes(table.tableName)) {
          this.commandTable = table.tableName;
          break;
        }
        this.commandTable = null;
      }
    }
  }

  /**
   * Valeur vide cohérente avec le widget rendu pour le champ : `false` pour un booléen,
   * `null` pour un dropdown à sélection unique, `[]` pour un autocomplete multiple.
   */
  private emptyValueForField(field: Field): any {
    if (field.variableType === 'boolean') return false;
    if (field.valueType === 'TAG' || field.variableType === 'list') {
      return SINGLE_SELECT_VALUE_TYPES.includes(field.valueType) ? null : [];
    }
    return '';
  }

  private initializeFormValues(): void {
    if (!this.conditionStep.values || this.conditionStep.values.length === 0) {
      this.isInitializing = false;
      return;
    }

    const field = this.field.value;

    if (!field) {
      this.isInitializing = false;
      return;
    }

    if (field.valueType === 'TAG' || field.variableType === 'list') {
      const items = this.conditionStep.values.map(value => ({ code: value, value: value }));
      // Le modèle attendu par dmp-select dépend du widget rendu : un objet pour le dropdown
      // (sélection unique), un tableau pour l'autocomplete multiple.
      const valueObjects = SINGLE_SELECT_VALUE_TYPES.includes(field.valueType)
        ? (items[0] ?? null)
        : items;
      this.conditionForm.setControl('value', this.createFormControl(valueObjects))
      this.isInitializing = false;
      return;
    }

    if(field.variableType === 'boolean'){
      const value = this.conditionStep.values[0] === 'true' ? true : false;
      this.conditionForm.setControl('value', this.createFormControl(value));
      this.isInitializing = false;
      return;
    }

    const operator = this.operator.value;

    if (!operator) {
      this.isInitializing = false;
      return;
    }

    // Conversion des valeurs selon le type
    if (this.isNumberRangeOperator()) {
      const values = this.conditionStep.values.slice(0, 2).map(v => {
        const num = parseFloat(v);
        return isNaN(num) ? null : num;
      });

      const rangeArray = this.formBuilder.array([
        this.createFormControl(values[0] || null),
        this.createFormControl(values[1] || null)
      ]);

      this.conditionForm.setControl('value', rangeArray);
      this.isInitializing = false;
      return;
    }

    try {
      let formValue: any = null;     

      if (field.variableType === 'number' && !this.isNumberRangeOperator()) {
        if (this.isChipsOperator()) {
          formValue = this.conditionStep.values
            .map(v => parseFloat(v))
            .filter(v => !isNaN(v));
        } else {
          const num = parseFloat(this.conditionStep.values[0]);
          formValue = isNaN(num) ? null : num;
        }
      } else if (field.variableType === 'date') {
        if (this.isDateExactMode()) {
          formValue = this.conditionStep.values
            .map(v => {
              try {
                return v && v.trim() ? new Date(v) : null;
              } catch {
                return null;
              }
            })
            .filter(v => v !== null);
        } else if (this.isDateRangeOperator()) {
          const values = this.conditionStep.values.slice(0, 2).map(v => {
            try {
              return v && v.trim() ? new Date(v) : null;
            } catch {
              return null;
            }
          });
          if (values.every(v => v === null)) {
            formValue = null;
          } else {
            formValue = [values[0], values[1]];
          }
        } else if (this.isDateSlidingRange()) {
            const unitValue = this.conditionStep.values[0];
            const deltaValue = parseInt(this.conditionStep.values[1]);
            formValue = [unitValue, isNaN(deltaValue) ? 0 : deltaValue]
        } else {
          try {
            formValue = this.conditionStep.values[0] ? new Date(this.conditionStep.values[0]) : null;
          } catch {
            formValue = null;
          }
        }
      } else {
        if (this.isChipsOperator()) {
          formValue = [...this.conditionStep.values];
        } else {
          formValue = this.conditionStep.values;
        }
      }

      this.conditionForm.setControl('value', this.createFormControl(formValue));

      if (this.isDateRangeOperator() || this.isDateSlidingRange()) {
        this.conditionForm.setControl('excludeYear', this.createFormControl(this.conditionStep.excludeYear || false));
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation des valeurs:', error);
    }

    this.isInitializing = false;
  }

  onOperatorChange(): void {
    if (this.readOnlyMode) return;

    if (this.field?.value && (this.field.value.valueType === 'TAG' || ['list', 'boolean'].includes(this.field.value.variableType))) {
      return;
    }
    if (!this.field?.value || !this.operator?.value) return;
    if (this.isNumberRangeOperator()) {
      const rangeArray = this.formBuilder.array([
        this.formBuilder.control(null), // min
        this.formBuilder.control(null)  // max
      ]);
      this.conditionForm.setControl('value', rangeArray);
    } else if (this.isDateExactMode() || this.isChipsOperator()) {
      this.conditionForm.setControl('value', this.formBuilder.control([]))
    } else {
      this.conditionForm.setControl('value', this.formBuilder.control(null));
    }
    this.conditionForm.patchValue({
      excludeYear: false
    })
  }

  // Méthodes utilitaires pour le template
  isRangeOperator(): boolean {
    if (!this.operator?.value || !this.field?.value) return false;

    const fieldType = this.field.value.variableType;
    const operatorCode = this.operator.value;

    return (fieldType === 'number' && operatorCode === ConditionIfStepDTO.OperatorEnum.NUMBERRANGE) || (fieldType === 'date' && operatorCode === ConditionIfStepDTO.OperatorEnum.DATERANGE);
  }

  isEmptyOperator(): boolean {
    return this.operator?.value?.trim().includes('EMPTY');
  }

  isChipsOperator(): boolean {
    if (!this.operator?.value || !this.field?.value) return false;

    const fieldType = this.field.value.variableType;
    const operatorCode = this.operator.value;

    if (fieldType === 'string') {
      return !this.isEmptyOperator();
    }

    if (fieldType === 'number') {
      const excludedOperators = [ConditionIfStepDTO.OperatorEnum.NUMBERGREATER, ConditionIfStepDTO.OperatorEnum.NUMBERGREATEREQUALS, ConditionIfStepDTO.OperatorEnum.NUMBERLESS, ConditionIfStepDTO.OperatorEnum.NUMBERLESSEQUALS, ConditionIfStepDTO.OperatorEnum.NUMBERRANGE, ConditionIfStepDTO.OperatorEnum.NUMBEREMPTY, ConditionIfStepDTO.OperatorEnum.NUMBERNOTEMPTY];
      return !excludedOperators.includes(operatorCode);
    }

    return false;
  }

  isNumberRangeOperator(): boolean {
    return this.field?.value?.variableType === 'number' && this.operator?.value === ConditionIfStepDTO.OperatorEnum.NUMBERRANGE;
  }

  isDateRangeOperator(): boolean {
    return this.field?.value?.variableType === 'date' && this.operator?.value === ConditionIfStepDTO.OperatorEnum.DATERANGE;
  }

  isDateExactMode(): boolean {
    return this.field?.value?.variableType === 'date' && this.operator?.value === ConditionIfStepDTO.OperatorEnum.DATEEXACTLY;
  }

  isDateSlidingRange(): boolean {
    return this.field?.value?.variableType === 'date' && this.operator?.value === ConditionIfStepDTO.OperatorEnum.DATESLIDINGRANGE;
  }

  validateNumber(event: any, inputElement: any): void {
    const value = event.value;
    if (isNaN(parseFloat(value))) {
      if (inputElement && inputElement.value && Array.isArray(inputElement.value)) {
        const validValues = inputElement.value.filter(v => !isNaN(parseFloat(v)));
        this.conditionForm.patchValue({ value: validValues });
      }
      console.warn('Valeur invalide pour un champ numérique:', value);
    }
  }
  onDeltaDateChange(value: any[]): void {
    this.conditionForm.get('value')?.setValue(value);
    this.markAsChanged();
    this.cdr.detectChanges();
  }


  private generateReadableDescription(field: any, operator: any, value: any, excludeYear?: boolean): string {
    if (!field) {
      return this.stepDescriptionService.condition(null, operator, value, excludeYear);
    }
    const table = this.availableFields.find(group => group.value === field.schema);
    return this.stepDescriptionService.condition({
      label: field.label || field.name,
      tableLabel: table?.label,
      variableType: field.variableType,
      valueType: field.valueType
    }, operator, value, excludeYear);
  }

  private getOperatorLabel(operatorValue: string): string {
    return this.stepDescriptionService.operatorLabel(operatorValue);
  }
}
